import type { ReactElement } from 'react';
import { memo } from 'react';

import { CopyTooltip } from '@bestseller-bit/frontend-community.components.copy-tooltip';
import { useIsLargeScreen } from '@bestseller-bit/frontend-community.theme.buying-theme';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface StyleHeaderProps {
    /** Style number shown above the name; copyable on click */
    styleNumber?: number;
    /** Style name, already resolved by the caller */
    styleName?: string | null;
    /** Optional composition line rendered under the name */
    composition?: string | null;
}

/**
 * Shared style header for the order info panel used by the order and sample order cards.
 * Purely presentational — the caller resolves the values.
 */
const StyleHeader = memo(
    ({
        styleNumber,
        styleName,
        composition,
    }: StyleHeaderProps): ReactElement => {
        const isLargeScreen = useIsLargeScreen();

        return (
            <Stack
                spacing={1}
                useFlexGap
            >
                <Stack sx={{ flexGrow: 1 }}>
                    <CopyTooltip>
                        {({ copyValue }): ReactElement => (
                            <Typography
                                variant="captions.default"
                                onClick={() => {
                                    if (styleNumber === undefined) return;
                                    void copyValue(styleNumber.toString());
                                }}
                            >
                                {styleNumber}
                            </Typography>
                        )}
                    </CopyTooltip>

                    <Typography
                        variant={
                            isLargeScreen
                                ? 'bodySmall.highlight'
                                : 'captions.highlight'
                        }
                    >
                        {styleName ?? 'Style name missing'}
                    </Typography>
                </Stack>

                {composition && (
                    <Typography
                        variant={
                            isLargeScreen
                                ? 'captions.default'
                                : 'captionsSmall.default'
                        }
                    >
                        {composition}
                    </Typography>
                )}
            </Stack>
        );
    }
);

StyleHeader.displayName = 'StyleHeader';

export { StyleHeader };
export type { StyleHeaderProps };
